/**
 * Display formatting. The business timezone is the library's (Asia/Kolkata by default);
 * every date shown to a person is DD-MM-YYYY (or "05 Mar 2026" in prose). Money is INR
 * with Indian digit grouping (12,34,567.50).
 */

export const LIBRARY_TIMEZONE = 'Asia/Kolkata';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

export interface DateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** Calendar parts of an ISO value in the library timezone. Date-only strings are never shifted. */
export function dateParts(value: string | Date | null | undefined, timeZone = LIBRARY_TIMEZONE): DateParts | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    const match = DATE_ONLY.exec(value);
    if (match) {
      return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]), hour: 0, minute: 0 };
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const read = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: read('year'), month: read('month'), day: read('day'), hour: read('hour') % 24, minute: read('minute') };
}

/** 05-03-2026 */
export function formatDate(value: string | Date | null | undefined, fallback = '-'): string {
  const p = dateParts(value);
  return p ? `${pad(p.day)}-${pad(p.month)}-${p.year}` : fallback;
}

/** 05 Mar 2026 */
export function formatDateLong(value: string | Date | null | undefined, fallback = '-'): string {
  const p = dateParts(value);
  return p ? `${pad(p.day)} ${MONTHS[p.month - 1]} ${p.year}` : fallback;
}

/** 05-03-2026 14:30 */
export function formatDateTime(value: string | Date | null | undefined, fallback = '-'): string {
  const p = dateParts(value);
  return p ? `${pad(p.day)}-${pad(p.month)}-${p.year} ${pad(p.hour)}:${pad(p.minute)}` : fallback;
}

/** 14:30 */
export function formatTime(value: string | Date | null | undefined, fallback = '-'): string {
  const p = dateParts(value);
  return p ? `${pad(p.hour)}:${pad(p.minute)}` : fallback;
}

/** Converts DD-MM-YYYY (also accepts / or . separators) to YYYY-MM-DD, or null when invalid. */
export function parseDmy(input: string): string | null {
  const match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(input.trim());
  if (!match) {
    return null;
  }
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) {
    return null;
  }
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** YYYY-MM-DD for "today" in the library timezone (or for the given instant). */
export function isoDate(value: Date = new Date()): string {
  const p = dateParts(value);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}` : '';
}

/** Add whole days to a YYYY-MM-DD string without timezone drift. */
export function addDays(iso: string, days: number): string {
  const p = dateParts(iso);
  if (!p) {
    return iso;
  }
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + days));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Whole days from `from` to `to` (YYYY-MM-DD strings). Negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  const a = dateParts(from);
  const b = dateParts(to);
  if (!a || !b) {
    return 0;
  }
  return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000);
}

const inrFull = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inrWhole = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const plainIn = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

/** ₹12,34,567.50. Pass { whole: true } to drop the paise. */
export function formatInr(value: number | null | undefined, options: { whole?: boolean } = {}): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  return (options.whole ? inrWhole : inrFull).format(value).replace(/\u00a0/g, '');
}

/** ₹1.2 L, ₹3.4 Cr: compact Indian units for dashboards. */
export function formatInrCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const trim = (n: number): string => n.toFixed(n >= 100 ? 0 : 1).replace(/\.0$/, '');
  if (abs >= 10_000_000) {
    return `${sign}₹${trim(abs / 10_000_000)} Cr`;
  }
  if (abs >= 100_000) {
    return `${sign}₹${trim(abs / 100_000)} L`;
  }
  if (abs >= 1_000) {
    return `${sign}₹${trim(abs / 1_000)}K`;
  }
  return `${sign}₹${trim(abs)}`;
}

/** 12,34,567 */
export function formatNumber(value: number | null | undefined): string {
  return value === null || value === undefined || Number.isNaN(value) ? '-' : plainIn.format(value);
}

export function formatPercent(value: number | null | undefined, digits = 0): string {
  return value === null || value === undefined || Number.isNaN(value) ? '-' : `${value.toFixed(digits)}%`;
}

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** "3 minutes ago", "in 2 days", "yesterday". */
export function relativeTime(value: string | Date | null | undefined, now: Date = new Date()): string {
  if (!value) {
    return '-';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(diffSeconds);
  if (abs < 45) {
    return 'just now';
  }
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['minute', 60],
    ['hour', 3600],
    ['day', 86_400],
    ['week', 604_800],
    ['month', 2_592_000],
    ['year', 31_536_000],
  ];
  let unit: Intl.RelativeTimeFormatUnit = 'minute';
  let size = 60;
  for (const [u, s] of steps) {
    if (abs >= s) {
      unit = u;
      size = s;
    }
  }
  return rtf.format(Math.round(diffSeconds / size), unit);
}

/** 3725 -> "1h 2m"; 59 -> "59s"; 0 -> "0s". */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) {
    return `${s}s`;
  }
  const days = Math.floor(s / 86_400);
  const hours = Math.floor((s % 86_400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (days) {
    parts.push(`${days}d`);
  }
  if (hours) {
    parts.push(`${hours}h`);
  }
  if (minutes && days === 0) {
    parts.push(`${minutes}m`);
  }
  return parts.join(' ') || '0s';
}

/** plural(1, 'item') -> "1 item"; plural(3, 'item') -> "3 items"; plural(2, 'copy', 'copies'). */
export function plural(count: number, singular: string, pluralForm?: string): string {
  return `${formatNumber(count)} ${count === 1 ? singular : (pluralForm ?? `${singular}s`)}`;
}

export function initials(name: string | null | undefined, max = 2): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '?';
  }
  return words
    .slice(0, max)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function fullName(person: { firstName?: string | null; surname?: string | null } | null | undefined): string {
  return [person?.firstName, person?.surname].filter(Boolean).join(' ').trim();
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[i]}`;
}

export function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

// ---------------------------------------------------------------------------------------------
// ISBN
// ---------------------------------------------------------------------------------------------

/** [registrant range lower, upper, publisher length] per registration group, after the 978 prefix. */
const ISBN_GROUPS: Record<string, { length: number; ranges: [number, number, number][] }> = {
  '0': { length: 1, ranges: [[0, 199, 2], [200, 699, 3], [700, 849, 4], [850, 899, 5], [900, 949, 6], [950, 999, 7]] },
  '1': { length: 1, ranges: [[0, 99, 2], [100, 399, 3], [400, 549, 4], [550, 799, 5], [800, 868, 6], [869, 998, 7]] },
  '81': { length: 2, ranges: [[0, 19, 2], [200, 699, 3], [700, 849, 4], [850, 899, 5], [900, 999, 7]] },
  '93': { length: 2, ranges: [[0, 9, 2], [100, 499, 3], [500, 799, 4], [800, 949, 5], [950, 999, 6]] },
  '3': { length: 1, ranges: [[0, 29, 2], [300, 899, 3], [900, 949, 4], [950, 989, 5], [990, 999, 6]] },
};

export function isbnCheckDigit13(first12: string): string {
  let sum = 0;
  for (let i = 0; i < 12; i += 1) {
    sum += Number(first12[i]) * (i % 2 === 0 ? 1 : 3);
  }
  return String((10 - (sum % 10)) % 10);
}

export function isbnCheckDigit10(first9: string): string {
  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    sum += Number(first9[i]) * (10 - i);
  }
  const check = (11 - (sum % 11)) % 11;
  return check === 10 ? 'X' : String(check);
}

export function isValidIsbn(raw: string): boolean {
  const s = raw.replace(/[\s-]/g, '').toUpperCase();
  if (/^\d{13}$/.test(s)) {
    return isbnCheckDigit13(s.slice(0, 12)) === s[12];
  }
  if (/^\d{9}[\dX]$/.test(s)) {
    return isbnCheckDigit10(s.slice(0, 9)) === s[9];
  }
  return false;
}

/** Converts a valid ISBN-10 to ISBN-13 (978 prefix); returns null for invalid input. */
export function isbn10to13(raw: string): string | null {
  const s = raw.replace(/[\s-]/g, '').toUpperCase();
  if (!/^\d{9}[\dX]$/.test(s) || isbnCheckDigit10(s.slice(0, 9)) !== s[9]) {
    return null;
  }
  const body = `978${s.slice(0, 9)}`;
  return `${body}${isbnCheckDigit13(body)}`;
}

/**
 * Hyphenates an ISBN (978-81-7525-766-5). Falls back to a simple prefix-body-check split for
 * registration groups outside the built-in table; returns the input untouched when it is not an ISBN.
 */
export function formatIsbn(raw: string | null | undefined): string {
  if (!raw) {
    return '';
  }
  let s = raw.replace(/[\s-]/g, '').toUpperCase();
  if (/^\d{9}[\dX]$/.test(s)) {
    s = isbn10to13(s) ?? s;
  }
  if (!/^97[89]\d{10}$/.test(s)) {
    return raw;
  }
  const prefix = s.slice(0, 3);
  const check = s[12];
  const rest = s.slice(3, 12);
  const groupKey = Object.keys(ISBN_GROUPS)
    .sort((a, b) => b.length - a.length)
    .find((k) => rest.startsWith(k));
  if (!groupKey || prefix !== '978') {
    return `${prefix}-${rest}-${check}`;
  }
  const group = ISBN_GROUPS[groupKey];
  const afterGroup = rest.slice(groupKey.length);
  const probe = Number(afterGroup.slice(0, 3).padEnd(3, '0'));
  const range = group.ranges.find(([lo, hi]) => probe >= lo && probe <= hi);
  if (!range) {
    return `${prefix}-${groupKey}-${afterGroup}-${check}`;
  }
  const publisher = afterGroup.slice(0, range[2]);
  const title = afterGroup.slice(range[2]);
  return `${prefix}-${groupKey}-${publisher}-${title}-${check}`;
}
